<script setup>
// DV (account 1): WhatsApp template picker and filler. Same payload as Chatwoot's
// WhatsAppTemplateParser (ReplyBox sends it), friendlier list and a live preview.
import { ref, computed, toRef, nextTick } from 'vue';
import { useAlert } from 'dashboard/composables';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { isWhatsAppComplete } from '@chatwoot/utils';
import {
  buildTemplateParameters,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
  renderTemplatePreview,
} from 'dashboard/helper/templateHelper';
import {
  DV_WA_TEMPLATE_GROUPS,
  dvTemplateCategoryLabel,
  dvTemplateGroup,
  dvTemplateTitle,
  dvVariableLabel,
  dvVariablePlaceholder,
  isDvTemplateVisible,
} from 'dashboard/helper/dvWaTemplates';

const props = defineProps({
  inboxId: { type: Number, default: undefined },
  sendRenderedContent: { type: Boolean, default: false },
});

const emit = defineEmits(['onSend', 'cancel']);

const store = useStore();
const query = ref('');
const group = ref('all');
const selected = ref(null);
const params = ref({});
const showErrors = ref(false);
const isRefreshing = ref(false);
const firstField = ref(null);

const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');
const templatesForInbox = useFunctionGetter(
  'inboxes/getFilteredWhatsAppTemplates',
  toRef(props, 'inboxId')
);

const bodyOf = template =>
  findComponentByType(template, COMPONENT_TYPES.BODY)?.text || '';
const headerOf = template =>
  findComponentByType(template, COMPONENT_TYPES.HEADER);
const footerOf = template =>
  findComponentByType(template, COMPONENT_TYPES.FOOTER)?.text || '';
const buttonsOf = template =>
  findComponentByType(template, COMPONENT_TYPES.BUTTONS)?.buttons || [];

const templates = computed(() =>
  (templatesForInbox.value || []).filter(isDvTemplateVisible)
);

const groupCounts = computed(() => {
  const counts = { all: templates.value.length };
  templates.value.forEach(template => {
    const key = dvTemplateGroup(template);
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
});

const visibleGroups = computed(() =>
  DV_WA_TEMPLATE_GROUPS.filter(
    item => item.key === 'all' || groupCounts.value[item.key]
  )
);

const filteredTemplates = computed(() => {
  const needle = query.value.trim().toLowerCase();
  return templates.value
    .filter(
      template =>
        group.value === 'all' || dvTemplateGroup(template) === group.value
    )
    .filter(template => {
      if (!needle) return true;
      return [dvTemplateTitle(template), template.name, bodyOf(template)]
        .join(' ')
        .toLowerCase()
        .includes(needle);
    })
    .sort((a, b) => dvTemplateTitle(a).localeCompare(dvTemplateTitle(b), 'ru'));
});

// "{{1}}" turns into a readable chip in the list preview.
const listPreview = template =>
  bodyOf(template).replace(
    /{{\s*([^}]+?)\s*}}/g,
    (_match, key) => `[${dvVariableLabel(bodyOf(template), key, template.name)}]`
  );

const firstName = value => String(value || '').trim().split(/\s+/)[0] || '';

const selectTemplate = async template => {
  selected.value = template;
  showErrors.value = false;
  const built = buildTemplateParameters(template);
  const body = bodyOf(template);
  Object.keys(built.body || {}).forEach(key => {
    const label = dvVariableLabel(body, key, template.name);
    if (label === 'Имя клиента') {
      built.body[key] = firstName(currentChat.value?.meta?.sender?.name);
    } else if (label === 'Имя менеджера') {
      built.body[key] = firstName(currentUser.value?.name);
    }
  });
  params.value = built;
  await nextTick();
  firstField.value?.[0]?.focus?.();
};

const backToList = () => {
  selected.value = null;
  params.value = {};
  showErrors.value = false;
};

const header = computed(() => (selected.value ? headerOf(selected.value) : null));
const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(header.value?.format)
);

const fields = computed(() => {
  if (!selected.value) return [];
  const body = bodyOf(selected.value);
  const headerText = header.value?.format === 'TEXT' ? header.value.text : '';
  const list = [];
  Object.keys(params.value.header || {}).forEach(key => {
    if (['media_url', 'media_type', 'media_name'].includes(key)) return;
    const label = dvVariableLabel(headerText, key, selected.value.name);
    list.push({ id: `header-${key}`, scope: 'header', key, label, placeholder: dvVariablePlaceholder(label) });
  });
  Object.keys(params.value.body || {}).forEach(key => {
    const label = dvVariableLabel(body, key, selected.value.name);
    list.push({ id: `body-${key}`, scope: 'body', key, label, placeholder: dvVariablePlaceholder(label) });
  });
  (params.value.buttons || []).forEach((button, index) => {
    if (!button) return;
    const text = buttonsOf(selected.value)[index]?.text || 'кнопки';
    list.push({
      id: `button-${index}`,
      scope: 'buttons',
      key: index,
      label: button.type === 'copy_code' ? `Код для «${text}»` : `Ссылка кнопки «${text}»`,
      placeholder: button.url ? button.url.replace(/{{[^}]+}}/g, '…') : 'Значение',
      hint: button.url || '',
    });
  });
  return list;
});

const fieldValue = field =>
  field.scope === 'buttons'
    ? params.value.buttons?.[field.key]?.parameter || ''
    : params.value[field.scope]?.[field.key] || '';

const setFieldValue = (field, value) => {
  if (field.scope === 'buttons') {
    params.value.buttons[field.key].parameter = value;
  } else {
    params.value[field.scope][field.key] = value;
  }
};

const isComplete = computed(
  () => !!selected.value && isWhatsAppComplete(selected.value, params.value)
);

// Body split into text and variable pieces for the WhatsApp-style preview.
const previewParts = text => {
  const parts = [];
  const source = String(text || '');
  let last = 0;
  source.replace(/{{\s*([^}]+?)\s*}}/g, (match, key, offset) => {
    if (offset > last) parts.push({ text: source.slice(last, offset) });
    const value = params.value.body?.[key] || '';
    parts.push({ key, value, label: dvVariableLabel(source, key, selected.value?.name) });
    last = offset + match.length;
    return match;
  });
  if (last < source.length) parts.push({ text: source.slice(last) });
  return parts;
};

const renderedBody = computed(() =>
  selected.value
    ? renderTemplatePreview(bodyOf(selected.value), params.value.body || {})
    : ''
);
const renderedHeader = computed(() =>
  header.value?.format === 'TEXT'
    ? renderTemplatePreview(header.value.text || '', params.value.header || {})
    : ''
);

const send = () => {
  if (!isComplete.value) {
    showErrors.value = true;
    return;
  }
  const { name, category, language, namespace } = selected.value;
  emit('onSend', {
    message: props.sendRenderedContent ? renderedBody.value : bodyOf(selected.value),
    pendingMessageContent: renderedBody.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      content_mode: props.sendRenderedContent ? 'rendered' : 'raw_template',
      processed_params: params.value,
    },
  });
};

const refreshTemplates = async () => {
  isRefreshing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inboxId);
    useAlert('Шаблоны обновлены из WhatsApp');
  } catch (error) {
    useAlert('Не получилось обновить шаблоны');
  } finally {
    isRefreshing.value = false;
  }
};

const nowLabel = new Date().toLocaleTimeString('ru-RU', {
  hour: '2-digit',
  minute: '2-digit',
});
</script>

<template>
  <div class="flex max-h-[85vh] min-h-[32rem] w-full flex-col" data-dv-wa-templates-modal>
    <header class="flex items-start gap-3 border-b border-n-weak px-5 py-4 sm:px-6">
      <button
        v-if="selected"
        type="button"
        class="-ml-1 mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
        aria-label="Назад к списку"
        @click="backToList"
      >
        <span class="i-lucide-arrow-left size-4" />
      </button>
      <span
        v-else
        class="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg bg-n-teal-3 text-n-teal-11"
      >
        <span class="i-ph-whatsapp-logo size-5" />
      </span>
      <div class="min-w-0 flex-1">
        <h2 class="truncate text-base font-semibold text-n-slate-12">
          {{ selected ? dvTemplateTitle(selected) : 'Шаблоны WhatsApp' }}
        </h2>
        <p class="mt-0.5 text-xs text-n-slate-11">
          {{
            selected
              ? 'Заполните поля — в превью видно, что получит клиент'
              : 'Одобренные Meta сообщения. Их можно отправить и после 24 часов тишины.'
          }}
        </p>
      </div>
      <button
        type="button"
        class="grid size-8 shrink-0 place-items-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
        aria-label="Закрыть"
        @click="emit('cancel')"
      >
        <span class="i-lucide-x size-4" />
      </button>
    </header>

    <!-- Step 1: list -->
    <template v-if="!selected">
      <div class="flex flex-col gap-3 px-5 pt-4 sm:px-6">
        <div class="flex gap-2">
          <label
            class="flex h-10 flex-1 items-center gap-2 rounded-xl bg-n-alpha-1 px-3 outline outline-1 outline-n-weak focus-within:outline-n-brand"
          >
            <span class="i-lucide-search size-4 shrink-0 text-n-slate-10" />
            <input
              v-model="query"
              type="search"
              placeholder="Найти: время, машина, оплата…"
              class="reset-base h-full w-full bg-transparent !text-sm text-n-slate-12 !outline-0"
            />
          </label>
          <button
            type="button"
            class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-alpha-1 text-n-slate-11 outline outline-1 outline-n-weak hover:bg-n-alpha-2 disabled:opacity-50"
            :disabled="isRefreshing"
            title="Обновить шаблоны из WhatsApp"
            @click="refreshTemplates"
          >
            <span class="i-lucide-refresh-ccw size-4" :class="{ 'animate-spin': isRefreshing }" />
          </button>
        </div>
        <div class="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
          <button
            v-for="item in visibleGroups"
            :key="item.key"
            type="button"
            class="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-xs font-medium transition-colors"
            :class="
              group === item.key
                ? 'bg-n-slate-12 text-n-solid-1'
                : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
            "
            @click="group = item.key"
          >
            {{ item.label }}
            <span class="tabular-nums opacity-70">{{ groupCounts[item.key] || 0 }}</span>
          </button>
        </div>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-2 sm:px-6">
        <div v-if="filteredTemplates.length" class="grid gap-2.5 sm:grid-cols-2">
          <button
            v-for="template in filteredTemplates"
            :key="`${template.name}-${template.language}`"
            type="button"
            class="group flex min-w-0 flex-col gap-2 rounded-2xl border border-n-weak bg-n-solid-1 p-3.5 text-left transition hover:border-n-teal-8 hover:shadow-sm"
            data-dv-wa-template
            @click="selectTemplate(template)"
          >
            <div class="flex items-start justify-between gap-2">
              <span class="text-sm font-semibold leading-5 text-n-slate-12">
                {{ dvTemplateTitle(template) }}
              </span>
              <span
                class="shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                :class="
                  String(template.category).toUpperCase() === 'MARKETING'
                    ? 'bg-n-amber-3 text-n-amber-11'
                    : 'bg-n-teal-3 text-n-teal-11'
                "
              >
                {{ dvTemplateCategoryLabel(template.category) }}
              </span>
            </div>
            <p class="line-clamp-3 text-xs leading-5 text-n-slate-11">
              {{ listPreview(template) }}
            </p>
            <div class="mt-auto flex flex-wrap items-center gap-1.5 text-[11px] text-n-slate-10">
              <span
                v-for="button in buttonsOf(template)"
                :key="button.text"
                class="inline-flex items-center gap-1 rounded-md bg-n-alpha-2 px-1.5 py-0.5"
              >
                <span
                  class="size-3"
                  :class="button.type === 'URL' ? 'i-lucide-external-link' : 'i-lucide-reply'"
                />
                {{ button.text }}
              </span>
              <span class="ml-auto font-mono opacity-60">{{ template.name }}</span>
            </div>
          </button>
        </div>
        <div v-else class="grid place-items-center py-16 text-center text-sm text-n-slate-11">
          <span class="i-lucide-search-x mb-2 size-6 opacity-60" />
          {{
            templates.length
              ? 'Ничего не нашлось. Попробуйте другое слово.'
              : 'У этого номера нет одобренных шаблонов.'
          }}
        </div>
      </div>
    </template>

    <!-- Step 2: fill and preview -->
    <template v-else>
      <div class="grid min-h-0 flex-1 overflow-y-auto md:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
        <section class="order-2 flex flex-col gap-4 px-5 py-5 sm:px-6 md:order-1">
          <div v-if="hasMediaHeader" class="flex flex-col gap-1.5">
            <label class="text-xs font-semibold text-n-slate-12">
              Ссылка на {{ header.format === 'IMAGE' ? 'картинку' : header.format === 'VIDEO' ? 'видео' : 'документ' }}
            </label>
            <input
              v-model="params.header.media_url"
              type="url"
              placeholder="https://…"
              class="reset-base h-10 rounded-xl bg-n-alpha-1 px-3 !text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
            />
            <input
              v-if="header.format === 'DOCUMENT'"
              v-model="params.header.media_name"
              type="text"
              placeholder="Имя файла"
              class="reset-base h-10 rounded-xl bg-n-alpha-1 px-3 !text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
            />
          </div>

          <div v-for="field in fields" :key="field.id" class="flex flex-col gap-1.5">
            <label :for="field.id" class="flex items-center justify-between gap-2 text-xs font-semibold text-n-slate-12">
              {{ field.label }}
              <span v-if="field.scope !== 'buttons'" class="font-mono text-[10px] font-normal text-n-slate-10">{{ '{' + '{' + field.key + '}' + '}' }}</span>
            </label>
            <input
              :id="field.id"
              ref="firstField"
              :value="fieldValue(field)"
              type="text"
              :placeholder="field.placeholder"
              class="reset-base h-10 rounded-xl bg-n-alpha-1 px-3 !text-sm text-n-slate-12 outline outline-1 transition-colors focus:outline-n-brand"
              :class="showErrors && !fieldValue(field) ? 'outline-n-ruby-8 bg-n-ruby-2' : 'outline-n-weak'"
              @input="setFieldValue(field, $event.target.value)"
              @keydown.enter.prevent="send"
            />
            <p v-if="field.hint" class="break-all text-[11px] text-n-slate-10">{{ field.hint }}</p>
          </div>

          <p v-if="!fields.length && !hasMediaHeader" class="rounded-xl bg-n-alpha-1 p-3 text-sm text-n-slate-11">
            В этом шаблоне нечего заполнять — можно отправлять.
          </p>
          <p v-if="showErrors && !isComplete" class="rounded-xl bg-n-ruby-3 p-3 text-xs font-medium text-n-ruby-11" role="alert">
            Заполните отмеченные поля — пустой шаблон WhatsApp не примет.
          </p>
        </section>

        <section class="order-1 border-b border-n-weak p-4 sm:p-5 md:order-2 md:border-b-0 md:border-l">
          <div
            class="flex h-full min-h-[14rem] flex-col justify-end rounded-2xl bg-[#efeae2] p-4 dark:bg-[#0b141a]"
            data-dv-wa-preview
          >
            <div class="ml-auto w-full max-w-[22rem]">
              <div class="rounded-xl rounded-tr-sm bg-[#d9fdd3] px-3 pb-1.5 pt-2 text-[13.5px] leading-[1.45] text-[#111b21] shadow-sm dark:bg-[#005c4b] dark:text-[#e9edef]">
                <p v-if="renderedHeader" class="mb-1 font-semibold">{{ renderedHeader }}</p>
                <p v-else-if="hasMediaHeader" class="mb-2 grid h-28 place-items-center rounded-lg bg-black/10 text-xs opacity-70">
                  {{ header.format }}
                </p>
                <p class="whitespace-pre-wrap break-words">
                  <template v-for="(part, index) in previewParts(bodyOf(selected))" :key="index">
                    <template v-if="part.text !== undefined">{{ part.text }}</template>
                    <span v-else-if="part.value" class="rounded bg-black/5 px-0.5 font-medium dark:bg-white/10">{{ part.value }}</span>
                    <span v-else class="rounded bg-[#ffd279]/60 px-1 text-[12px] font-medium text-[#5c4300]">{{ part.label }}</span>
                  </template>
                </p>
                <p v-if="footerOf(selected)" class="mt-1 text-[12px] opacity-60">{{ footerOf(selected) }}</p>
                <p class="mt-0.5 text-right text-[11px] opacity-60">{{ nowLabel }} ✓✓</p>
              </div>
              <div v-if="buttonsOf(selected).length" class="mt-1 grid gap-1">
                <span
                  v-for="button in buttonsOf(selected)"
                  :key="button.text"
                  class="flex items-center justify-center gap-1.5 rounded-xl bg-white/90 py-2 text-[13px] font-medium text-[#027eb5] shadow-sm dark:bg-[#1f2c34] dark:text-[#53bdeb]"
                >
                  <span class="size-3.5" :class="button.type === 'URL' ? 'i-lucide-external-link' : 'i-lucide-reply'" />
                  {{ button.text }}
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>

      <footer class="flex items-center justify-between gap-3 border-t border-n-weak px-5 py-3 sm:px-6">
        <span class="hidden text-xs text-n-slate-10 sm:inline">
          {{ dvTemplateCategoryLabel(selected.category) }} · {{ selected.language }} · <span class="font-mono">{{ selected.name }}</span>
        </span>
        <div class="ml-auto flex gap-2">
          <button
            type="button"
            class="h-10 rounded-xl px-4 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
            @click="backToList"
          >
            Другой шаблон
          </button>
          <button
            type="button"
            class="inline-flex h-10 items-center gap-2 rounded-xl bg-[#25d366] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1ebe5b] disabled:opacity-50"
            data-dv-wa-send
            @click="send"
          >
            <span class="i-lucide-send size-4" />
            Отправить
          </button>
        </div>
      </footer>
    </template>
  </div>
</template>
